// Profile.jsx - Fixed version
import React, { useState, useEffect, useContext } from 'react';
import './Profile.css';
import api, { fetchWorkspaces } from '../api/api';
import { AuthContext } from '../context/AuthContext';

const Profile = () => {
  const { user, updateProfileImage } = useContext(AuthContext);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Form states
  const [username, setUsername] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Workspace states
  const [ownedWorkspaces, setOwnedWorkspaces] = useState([]);
  const [memberWorkspaces, setMemberWorkspaces] = useState([]);

  useEffect(() => {
    if (user) {
      loadUserData();
      loadWorkspaces();
    }
  }, [user]);

  const loadUserData = () => {
    if (!user) return;
    
    setUsername(user.name || '');
    
    // Load profile image from localStorage
    const savedImage = localStorage.getItem(`profile_image_${user.id}`);
    if (savedImage) {
      setProfileImage(savedImage);
      setImagePreview(savedImage);
    } else if (user.avatar) {
      setProfileImage(user.avatar);
      setImagePreview(user.avatar);
    }
  };

  const loadWorkspaces = async () => {
    try {
      const userId = user?.id;
      if (!userId) {
        setLoading(false);
        return;
      }
      
      // This would need adjustment based on your actual API structure
      // For now, we'll fetch all workspaces and filter them
      const allWorkspaces = await fetchWorkspaces(userId);
      
      // Filter owned workspaces (assuming workspace.owner_id matches user.id)
      const owned = Array.isArray(allWorkspaces) ? 
        allWorkspaces.filter(ws => ws.owner_id === userId) : [];
      setOwnedWorkspaces(owned);
      
      // Filter member workspaces (where user is a member but not owner)
      const member = Array.isArray(allWorkspaces) ? 
        allWorkspaces.filter(ws => ws.owner_id !== userId) : [];
      setMemberWorkspaces(member);
      
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'U';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result;
        setProfileImage(imageData);
        setImagePreview(imageData);
        
        // Save to localStorage and update AuthContext
        if (user?.id) {
          localStorage.setItem(`profile_image_${user.id}`, imageData);
          updateProfileImage(user.id, imageData);
        }
        
        alert('Profile image saved successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateUsername = async () => {
    if (!username || !username.trim()) {
      alert('Username cannot be empty');
      return;
    }

    try {
      // Call your API to update username
      await api.put('/users/update-username', {
        name: username
      });
      
      // Update localStorage and context
      const updatedUser = { ...user, name: username };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      alert('Username updated successfully!');
    } catch (error) {
      console.error('Error updating username:', error);
      alert('Failed to update username');
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('All password fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      alert('New password must be at least 6 characters');
      return;
    }

    try {
      // Call your API to change password
      await api.post('/users/change-password', {
        currentPassword,
        newPassword
      });
      
      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      alert('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please check your current password.');
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-error">
        <h2>User Not Found</h2>
        <p>Please log in to access your profile.</p>
        <a href="/login" className="login-link">Go to Login</a>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your account information and workspace memberships</p>
      </div>

      <div className="profile-content">
        {/* Left sidebar */}
        <div className="profile-sidebar">
          <div className="profile-card">
            <div className="profile-image-container">
              {imagePreview ? (
                <img 
                  src={imagePreview} 
                  alt="Profile" 
                  className="profile-image"
                />
              ) : (
                <div className="profile-initials">
                  {getInitials(user?.name)}
                </div>
              )}
              <label className="image-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="image-upload-input"
                />
                <span className="upload-text">Change Photo</span>
              </label>
            </div>
            
            <div className="profile-info">
              <h2>{user?.name || 'User'}</h2>
              <p className="user-email">{user?.email || 'No email'}</p>
              <p className="user-role">Role: {user?.role || 'admin'}</p>
            </div>
          </div>

          <nav className="profile-nav">
            <button 
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile Settings
            </button>
            <button 
              className={`nav-item ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              Security
            </button>
            <button 
              className={`nav-item ${activeTab === 'workspaces' ? 'active' : ''}`}
              onClick={() => setActiveTab('workspaces')}
            >
              Workspaces
            </button>
          </nav>
        </div>

        {/* Right content area */}
        <div className="profile-main">
          {activeTab === 'profile' && (
            <div className="profile-section">
              <h3>Profile Information</h3>
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="form-input"
                  />
                  <button 
                    onClick={handleUpdateUsername}
                    className="update-btn"
                    disabled={!username?.trim() || username === user?.name}
                  >
                    Update
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={user?.email || ''}
                  readOnly
                  className="form-input readonly"
                />
                <small className="hint">Email cannot be changed</small>
              </div>

              <div className="form-group">
                <label htmlFor="role">Account Role</label>
                <input
                  type="text"
                  id="role"
                  value={user?.role || 'Admin'}
                  readOnly
                  className="form-input readonly"
                />
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="profile-section">
              <h3>Change Password</h3>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="form-input"
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="form-input"
                  placeholder="Enter new password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="form-input"
                  placeholder="Confirm new password"
                />
              </div>

              <button 
                onClick={handleChangePassword}
                className="change-password-btn"
                disabled={
                  !passwordData.currentPassword ||
                  !passwordData.newPassword ||
                  !passwordData.confirmPassword ||
                  passwordData.newPassword !== passwordData.confirmPassword
                }
              >
                Change Password
              </button>
            </div>
          )}

          {activeTab === 'workspaces' && (
            <div className="profile-section">
              <h3>Workspace Memberships</h3>
              
              {/* Owned Workspaces */}
              <div className="workspace-section">
                <h4>Owned Workspaces</h4>
                {ownedWorkspaces.length > 0 ? (
                  <div className="workspace-list">
                    {ownedWorkspaces.map(workspace => (
                      <div key={workspace.id} className="workspace-card">
                        <div className="workspace-header">
                          <h5>{workspace.name || `Workspace ${workspace.id}`}</h5>
                          <span className="badge owner">Owner</span>
                        </div>
                        <p className="workspace-id">ID: {workspace.id}</p>
                        <p className="workspace-info">
                          Created: {new Date(workspace.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-workspaces">You don't own any workspaces yet.</p>
                )}
              </div>

              {/* Member Workspaces */}
              <div className="workspace-section">
                <h4>Member Workspaces</h4>
                {memberWorkspaces.length > 0 ? (
                  <div className="workspace-list">
                    {memberWorkspaces.map(workspace => (
                      <div key={workspace.id} className="workspace-card">
                        <div className="workspace-header">
                          <h5>{workspace.name || `Workspace ${workspace.id}`}</h5>
                          <span className="badge member">Member</span>
                        </div>
                        <p className="workspace-id">ID: {workspace.id}</p>
                        <p className="workspace-info">
                          Owner ID: {workspace.owner_id}
                        </p>
                        <p className="workspace-info">
                          Joined: {new Date(workspace.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-workspaces">You are not a member of any workspaces.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;